/**
 * Detection Rules v2 — Suricata per-type hooks + types.
 *
 * Backing routes: cmd/admin-bff/rules_suricata.go (commit f80501f).
 * Per-rule routes use a literal /r/ discriminator to dodge the Go 1.22
 * mux ambiguity issue first hit by the YARA vertical.
 *
 * Query key convention:
 *   ["rules","suricata","list",filters]
 *   ["rules","suricata","detail",id]
 *   ["rules","suricata","corpora"]
 *   ["rules","suricata","next-sid"]
 *   ["rules","suricata","imports"]
 *   ["rules","suricata","imports",id]
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./api";
import type { LintMessage, Rule, RuleSeverity, RuleTLP } from "./rules";

// ---------- Types ----------

export type SuricataAction = "alert" | "drop" | "reject" | "log" | "pass";
export type SuricataDirection = "->" | "<>" | "<-" | "any" | "to_server" | "to_client";
export type SIDRangeOwner =
    | "official"
    | "snort"
    | "et-open"
    | "et-pro"
    | "proofpoint"
    | "custom";

export const SURICATA_ACTIONS: ReadonlyArray<{ value: SuricataAction; label: string }> = [
    { value: "alert", label: "alert" },
    { value: "drop", label: "drop" },
    { value: "reject", label: "reject" },
    { value: "log", label: "log" },
    { value: "pass", label: "pass" },
];

export const SURICATA_PROTOCOLS: ReadonlyArray<string> = [
    "tcp", "udp", "icmp", "ip", "http", "dns", "tls", "ssl", "quic",
    "smb", "ssh", "ftp", "imap", "pop3", "smtp", "snmp", "rdp", "modbus",
];

export const SURICATA_DIRECTIONS: ReadonlyArray<{ value: SuricataDirection; label: string }> = [
    { value: "->", label: "→ (to dest)" },
    { value: "<>", label: "↔ (bidir)" },
    { value: "<-", label: "← (from dest)" },
];

export const SURICATA_CLASSTYPES: ReadonlyArray<string> = [
    "attempted-admin", "attempted-user", "attempted-recon", "attempted-dos",
    "successful-admin", "successful-user", "successful-recon-largescale",
    "trojan-activity", "command-and-control", "exploit-kit", "policy-violation",
    "malware-cnc", "credential-theft", "domain-c2", "shellcode-detect",
    "denial-of-service", "web-application-attack", "web-application-activity",
    "network-scan", "suspicious-filename-detect", "bad-unknown",
    "not-suspicious", "unknown",
];

export const SID_RANGES: ReadonlyArray<{
    value: SIDRangeOwner;
    label: string;
    chip: string;
}> = [
    { value: "snort", label: "Snort", chip: "chip--subtle" },
    { value: "et-open", label: "ET Open", chip: "chip--info" },
    { value: "et-pro", label: "ET Pro", chip: "chip--info-strong" },
    { value: "proofpoint", label: "Proofpoint", chip: "chip--warn" },
    { value: "custom", label: "Custom", chip: "chip--accent" },
];

export interface SuricataMeta {
    ruleId: string;
    action: SuricataAction;
    protocol: string;
    sourceNet: string;
    sourcePort: string;
    direction: SuricataDirection;
    destNet: string;
    destPort: string;
    sid: number;
    rev: number;
    classtype?: string;
    priority?: number;
    msg?: string;
    reference: string[];
    metadata: string[];
    mitreTacticId?: string;
    mitreTechniqueId?: string;
    cve: string[];
    malwareFamily?: string;
    flowbits: string[];
    flowbitsSet: string[];
    flowbitsIsset: string[];
    thresholding?: unknown;
    sidRangeOwner: SIDRangeOwner;
}

export interface SuricataListRow {
    id: string;
    slug: string;
    name: string;
    severity: string;
    status: string;
    currentVersion: number;
    publishedVersion?: number;
    tags: string[];
    imported: boolean;
    license?: string;
    updatedAt: string;
    action: SuricataAction;
    protocol: string;
    direction: SuricataDirection;
    sourceNet: string;
    sourcePort: string;
    destNet: string;
    destPort: string;
    sid: number;
    classtype?: string;
    sidRangeOwner: SIDRangeOwner;
}

export interface SuricataListFilters {
    q?: string;
    protocol?: string;
    direction?: SuricataDirection | "";
    classtype?: string;
    sidRange?: SIDRangeOwner | "";
    mitreTactic?: string;
    malwareFamily?: string;
    status?: string;
    imported?: boolean;
    tag?: string;
    limit?: number;
    offset?: number;
}

/**
 * Detail response. Backend returns `{rule}` where `rule.meta.suricata` carries
 * the per-type meta; we normalise that here so the page can read meta + rule
 * uniformly. Versions, recent runs and cached lint are fetched lazily.
 */
export interface SuricataDetail {
    rule: Rule;
    suricataMeta: SuricataMeta;
    currentVersionContent: string;
    currentLintMessages: LintMessage[];
}

export interface SuricataCorpus {
    slug: string;
    name: string;
    kind: string;
    description: string;
    itemCount: number;
}

export interface SuricataDryRunMatch {
    sid: number;
    signature: string;
    proto: string;
    srcIp: string;
    dstIp: string;
    srcPort: number;
    dstPort: number;
    timestamp: string;
}

export interface SuricataDryRunResult {
    engine: "suricata-container" | "unavailable" | string;
    matches: number;
    alerts: SuricataDryRunMatch[];
    elapsedMs: number;
    note?: string;
}

export interface SuricataImportRow {
    id: string;
    registrySlug: string;
    workflowId?: string;
    runId?: string;
    status: "running" | "ok" | "error" | "cancelled" | "unknown";
    counters?: Record<string, unknown>;
    startedAt?: string;
    finishedAt?: string;
    elapsedMs?: number;
}

export interface NextSIDResponse {
    sid: number;
    sidRangeOwner: SIDRangeOwner;
}

// ---------- helpers ----------

function toQuery(filters: SuricataListFilters): string {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(filters)) {
        if (v === undefined || v === null || v === "") continue;
        p.set(k, String(v));
    }
    return p.toString();
}

export function ownerForSID(sid: number): SIDRangeOwner {
    if (sid >= 1 && sid <= 1_999_999) return "snort";
    if (sid >= 2_000_000 && sid <= 2_099_999) return "et-open";
    if (sid >= 2_100_000 && sid <= 2_299_999) return "et-pro";
    if (sid >= 2_300_000 && sid <= 4_999_999) return "proofpoint";
    if (sid >= 5_000_000 && sid <= 5_999_999) return "custom";
    return "custom";
}

// ---------- Queries ----------

export function useSuricataList(filters: SuricataListFilters = {}) {
    return useQuery({
        queryKey: ["rules", "suricata", "list", filters] as const,
        queryFn: () =>
            api.get<{ rules: SuricataListRow[]; total: number }>(
                `/api/v2/rules/suricata?${toQuery(filters)}`,
            ),
    });
}

export function useSuricataDetail(id: string | undefined) {
    return useQuery({
        queryKey: ["rules", "suricata", "detail", id] as const,
        queryFn: async (): Promise<SuricataDetail> => {
            const data = await api.get<{ rule: Rule & { meta: { suricata?: SuricataMeta } } }>(
                `/api/v2/rules/suricata/r/${id}`,
            );
            const meta = (data.rule.meta?.suricata ?? null) as SuricataMeta | null;
            return {
                rule: data.rule,
                suricataMeta:
                    meta ?? {
                        ruleId: data.rule.id,
                        action: "alert",
                        protocol: "tcp",
                        sourceNet: "$HOME_NET",
                        sourcePort: "any",
                        direction: "->",
                        destNet: "$EXTERNAL_NET",
                        destPort: "any",
                        sid: 0,
                        rev: 1,
                        reference: [],
                        metadata: [],
                        cve: [],
                        flowbits: [],
                        flowbitsSet: [],
                        flowbitsIsset: [],
                        sidRangeOwner: "custom",
                    },
                currentVersionContent: "",
                currentLintMessages: [],
            };
        },
        enabled: !!id,
    });
}

export function useSuricataCorpora() {
    return useQuery({
        queryKey: ["rules", "suricata", "corpora"] as const,
        queryFn: () =>
            api
                .get<{ corpora: SuricataCorpus[] }>(`/api/v2/rules/suricata/corpora`)
                .then((d) => d.corpora ?? []),
        staleTime: 60_000,
    });
}

export function useSuricataNextSID() {
    return useQuery({
        queryKey: ["rules", "suricata", "next-sid"] as const,
        queryFn: () => api.get<NextSIDResponse>(`/api/v2/rules/suricata/next-sid`),
        staleTime: 0,
    });
}

export function useSuricataImports() {
    return useQuery({
        queryKey: ["rules", "suricata", "imports"] as const,
        queryFn: () =>
            api
                .get<{ imports: SuricataImportRow[] }>(`/api/v2/rules/suricata/imports`)
                .then((d) => d.imports ?? []),
        refetchInterval: 5_000,
    });
}

export function useSuricataImport(id: string | undefined) {
    return useQuery({
        queryKey: ["rules", "suricata", "imports", id] as const,
        queryFn: () =>
            api
                .get<{ importId: string; status: string }>(
                    `/api/v2/rules/suricata/imports/${id}`,
                )
                .then((d) => ({
                    id: d.importId,
                    registrySlug: "",
                    status: (d.status ?? "unknown") as SuricataImportRow["status"],
                }) satisfies SuricataImportRow),
        enabled: !!id,
        refetchInterval: 3_000,
    });
}

// ---------- Mutations ----------

export interface CreateSuricataInput {
    slug?: string;
    name: string;
    description: string;
    severity: RuleSeverity;
    tlp: RuleTLP;
    tags: string[];
    content: string;
    note?: string;
    sid?: number;
    action?: SuricataAction;
    protocol?: string;
    sourceNet?: string;
    sourcePort?: string;
    direction?: SuricataDirection;
    destNet?: string;
    destPort?: string;
    classtype?: string;
    priority?: number;
}

export function useCreateSuricata() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input: CreateSuricataInput) =>
            api.post<{ rule: Rule & { meta: { suricata?: SuricataMeta } } }>(
                `/api/v2/rules/suricata`,
                input,
            ),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["rules", "suricata", "list"] });
            void qc.invalidateQueries({ queryKey: ["rules", "suricata", "next-sid"] });
        },
    });
}

export interface PatchSuricataInput {
    name?: string;
    severity?: RuleSeverity;
    tlp?: RuleTLP;
    tags?: string[];
    mitreTactics?: string[];
    mitreTechniques?: string[];
    description?: string;
    status?: string;
    meta?: {
        action?: SuricataAction;
        protocol?: string;
        sourceNet?: string;
        sourcePort?: string;
        direction?: SuricataDirection;
        destNet?: string;
        destPort?: string;
        rev?: number;
        classtype?: string;
        priority?: number;
        msg?: string;
        mitreTacticId?: string;
        mitreTechniqueId?: string;
        malwareFamily?: string;
        reference?: string[];
        metadata?: string[];
        cve?: string[];
    };
}

export function usePatchSuricata(id: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (patch: PatchSuricataInput) =>
            api.patch<{ rule: Rule }>(`/api/v2/rules/suricata/r/${id}`, patch),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["rules", "suricata", "detail", id] });
            void qc.invalidateQueries({ queryKey: ["rules", "suricata", "list"] });
        },
    });
}

export function usePutSuricataContent(id: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input: { content: string; note?: string }) =>
            api.put<{
                version: number;
                lintStatus: string;
                lintMessages: LintMessage[];
                meta: SuricataMeta;
            }>(`/api/v2/rules/suricata/r/${id}/content`, input),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["rules", "suricata", "detail", id] });
            void qc.invalidateQueries({ queryKey: ["rules", "suricata", "list"] });
        },
    });
}

export function usePutSuricataSID(id: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input: { sid: number }) =>
            api.put<{ sid: number; sidRangeOwner: SIDRangeOwner }>(
                `/api/v2/rules/suricata/r/${id}/sid`,
                input,
            ),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["rules", "suricata", "detail", id] });
            void qc.invalidateQueries({ queryKey: ["rules", "suricata", "list"] });
        },
    });
}

export function useLintSuricata(id: string | undefined) {
    return useMutation({
        mutationFn: (input: { content: string; container?: boolean }) =>
            api.post<{
                lintStatus: string;
                lintMessages: LintMessage[];
                containerUsed: boolean;
            }>(`/api/v2/rules/suricata/r/${id}/lint`, input),
    });
}

export function useDryRunSuricata(id: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input: { corpusSlug: string; pcapName?: string }) =>
            api.post<SuricataDryRunResult>(`/api/v2/rules/suricata/r/${id}/dry-run`, input),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["rules", "suricata", "detail", id] });
        },
    });
}

export function useStartSuricataImport() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input: { registrySlug: string }) =>
            api.post<{ importId: string; workflowId: string; runId: string }>(
                `/api/v2/rules/suricata/imports`,
                input,
            ),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["rules", "suricata", "imports"] });
        },
    });
}

// ---------- Constants ----------

export const SURICATA_REGISTRIES: ReadonlyArray<{
    slug: string;
    name: string;
    license: string;
    description: string;
    warning?: string;
}> = [
    {
        slug: "oisf/suricata-emerging-threats",
        name: "ET Open (Emerging Threats)",
        license: "BSD-3-Clause",
        description:
            "Proofpoint Emerging Threats Open ruleset. Free for community use; BSD-3-Clause.",
        warning: "ET Open ships ~30,000+ rules. The first import can take several minutes.",
    },
];

/** Validate a Suricata port spec. Accepts "any", an integer, or a range
 *  like "1024:" or "1024:65535" or "[80,443]". */
export function validatePortSpec(s: string): boolean {
    const v = s.trim();
    if (v === "" || v === "any") return true;
    if (/^\d+$/.test(v)) {
        const n = Number(v);
        return n >= 0 && n <= 65535;
    }
    if (/^\d+:\d*$/.test(v) || /^:\d+$/.test(v)) return true;
    if (/^\[[\d,\s:!]+\]$/.test(v)) return true;
    return false;
}
